import React, { useEffect, useMemo, useState } from 'react';
import './ReportsCard.scss';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import {
  Button,
  Dropdown,
  DropdownOption,
  StyledCalendar,
} from '@orderly/morrisons-component-library';
import {
  getCompletedOrdersReportRequest,
  getConfirmedOrdersReportRequest,
  getProcessedBulkOrdersReportRequest,
  getCustomerItemSalesReportRequest,
  getCustomerOrderSalesReportRequest,
  resetReportData,
} from '../../actions/Reports';
import { IConfirmedOrdersParams } from '../../api/Reports/ConfirmedOrders/IConfirmedOrdersParams';
import { IConfirmedOrdersResponse } from '../../api/Reports/ConfirmedOrders/IConfirmedOrdersResponse';
import { ICustomerItemSalesReportParams } from '../../api/Reports/CustomerSales/ICustomerItemSalesParams';
import { ICustomerOrderSalesReportParams } from '../../api/Reports/CustomerSales/ICustomerOrderSalesParams';
import { ICompletedOrdersParams } from '../../api/Reports/CompletedOrders/ICompletedOrdersParams';
import { getNormalizedEnumString } from '../../helpers/EnumHelper';
import {
  IAdminRetailers,
  IApiRequest,
  IRegion,
  IReport,
  IStoreState,
  IUserDetails,
  OrderRetailerType,
} from '../../types';
import LoadingThrobber from '../LoadingThrobber/LoadingThrobber';
import Modal from '../Modal/Modal';
import { isAccountManager, isAdmin } from '../../helpers/Users/UserHelper';
import FormatDateHelper from '../../helpers/Format/Date/FormatDateHelper';
import { GenerateDownload } from '../../helpers/DownloadHelper';

interface IReportsCardProps {
  userDetails?: IUserDetails;
  confirmedOrdersCsvDownload?: IApiRequest<IReport>;
  retailers?: IApiRequest<IAdminRetailers>;
  regions: IRegion[];
  downloadConfirmedOrdersCsv?: (
    parameters: IConfirmedOrdersParams,
  ) => Promise<IConfirmedOrdersResponse>;
  downloadCompletedOrdersReport?: (
    parameters: ICompletedOrdersParams,
    onSuccess: (data: any) => void,
  ) => void;
  clearConfirmedOrdersCsvDownload?: () => void;
  exportBulkManifest?: (onSuccess: (data: any) => void) => void;
  downloadCustomerOrderSalesCsv?: (
    parameters: ICustomerOrderSalesReportParams,
    onSuccess: (data: any) => void,
  ) => void;
  downloadCustomerItemSalesCsv?: (
    parameters: ICustomerItemSalesReportParams,
    onSuccess: (data: any) => void,
  ) => void;
}

const ReportsCard = ({
  userDetails,
  confirmedOrdersCsvDownload,
  retailers,
  regions,
  downloadConfirmedOrdersCsv,
  downloadCompletedOrdersReport,
  clearConfirmedOrdersCsvDownload,
  exportBulkManifest,
  downloadCustomerOrderSalesCsv,
  downloadCustomerItemSalesCsv,
}: IReportsCardProps) => {
  const todaysDate = new Date();

  const minimumReportDate = new Date(todaysDate);
  minimumReportDate.setMonth(minimumReportDate.getMonth() - 3);
  const maximumReportDate = new Date(todaysDate);
  maximumReportDate.setMonth(maximumReportDate.getMonth() + 3);

  const [showConfirmedOrderModal, setShowConfirmedOrderModal] = useState<boolean>(false);
  const [showOrderSalesModal, setShowOrderSalesModal] = useState<boolean>(false);
  const [customerSalesDate, setCustomerSalesDate] = useState<Date>(todaysDate);
  const [customerSalesFromDate, setCustomerSalesFromDate] = useState<Date>(todaysDate);
  const [customerSalesToDate, setCustomerSalesToDate] = useState<Date>(todaysDate);
  const [orderType, setOrderType] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<number>(-1);
  const [showItemSalesModal, setShowItemSalesModal] = useState<boolean>(false);
  const [showCompletedOrdersModal, setShowCompletedOrdersModal] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [selectedRetailer, setSelectedRetailer] = useState<string>('');
  const [pin, setPin] = useState<string>('');
  const initialRetailerOptions: DropdownOption[] = [{ value: '', label: 'All' }];
  const [retailerOptions, setRetailerOptions] = useState<DropdownOption[]>(initialRetailerOptions);
  const [calendarKey, setCalendarKey] = useState(0);

  const resetStates = () => {
    setShowConfirmedOrderModal(false);
    setShowOrderSalesModal(false);
    setCustomerSalesFromDate(todaysDate);
    setCustomerSalesToDate(todaysDate);
    setCustomerSalesDate(todaysDate);
    setCalendarKey(prevKey => prevKey + 1);
    setOrderType('');
    setSelectedRegion(-1);

    setShowItemSalesModal(false);
    setShowCompletedOrdersModal(false);

    setSelectedRetailer('');
    setPin('');
  };

  useEffect(() => {
    if (confirmedOrdersCsvDownload && confirmedOrdersCsvDownload.data) {
      createConfirmedOrderDownload(confirmedOrdersCsvDownload.data);
    }
  }, [confirmedOrdersCsvDownload]);

  useEffect(
    () => {
      if (isAccountManager(userDetails) && userDetails.regionId !== undefined) {
        setSelectedRegion(userDetails.regionId);
      }
    },
    [userDetails],
  );

  useEffect(() => {
    if (isAdmin(userDetails) || isAccountManager(userDetails)) {
      getLoadedRetailers();
    }
  }, [selectedRegion]);

  const getLoadedRetailers = () => {
    if (!retailers.loading && retailers.data) {
      const uniqueRetailerIds = new Set();
      const retailerList = [{ value: '', label: 'All' }];

      retailers.data.retailers.forEach((retailer) => {
        retailer.stores
          .forEach(() => {
            if (!uniqueRetailerIds.has(retailer.id)) {
              uniqueRetailerIds.add(retailer.id);
              retailerList.push({
                value: retailer.id.toString(),
                label: retailer.externalIdentifier,
              });
            }
          });
      });

      setSelectedRetailer('');
      setRetailerOptions(retailerList);
    }
  };

  const downloadConfirmedOrderCsv = async (retailerId: number, orderType: string) => {
    try {
      await downloadConfirmedOrdersCsv({ retailerId, orderType });
    } catch (error) {
      console.log('An error has occurred', error);
    }
  };

  const onExportCompletedOrdersReport = (): void => {
    setExportLoading(true);
    downloadCompletedOrdersReport({
      retailer: selectedRetailer,
      item: pin,
      fromDate: FormatDateHelper.formatCalendarDateToISO(customerSalesFromDate),
      toDate: FormatDateHelper.formatCalendarDateToISO(customerSalesToDate),
      regionId: selectedRegion,
    }, (response: any) => {
      const file = response.report;
      GenerateDownload(file.content, file.filename);
      setExportLoading(false);
    });
  };

  const createConfirmedOrderDownload = ({ filename, content }: IReport): void => {
    GenerateDownload(content, filename);
    clearConfirmedOrdersCsvDownload();
  };

  const enableExportBulkManifest = (): boolean => {
    const formattedTime = new Intl.DateTimeFormat('en-GB', {
      timeZoneName: 'longOffset',
      timeZone: 'Europe/London',
    }).format(todaysDate);
    const cutOff = new Date(todaysDate);
    cutOff.setUTCHours(10, 0, 0);

    const hasOffset = formattedTime.indexOf('+') !== -1;
    if (hasOffset) {
      const offset = formattedTime.split('+')[1].split(':')[0];
      const parsedOffset = parseInt(offset, 10);
      cutOff.setUTCHours(10 - parsedOffset, 0, 0);
    }

    return cutOff < todaysDate;
  };

  const onExportBulkManifest = (): void => {
    setExportLoading(true);
    exportBulkManifest((response: any) => {
      const file = response.processedBulkOrders;
      GenerateDownload(file.content, file.filename);
      setExportLoading(false);
    });
  };

  const regionOptions: DropdownOption[] = [
    { value: -1, label: 'All' },
    ...regions.map((region: IRegion) => ({
      value: region.id,
      label: getNormalizedEnumString(region.name),
    })),
  ];

  const orderTypeOptions = useMemo(() => {
    return [
      { value: '', label: 'All' },
      ...Object.keys(OrderRetailerType)
        .filter(key => isNaN(Number(key)))
        .map(key => ({
          value: OrderRetailerType[key as keyof typeof OrderRetailerType].toString(),
          label: getNormalizedEnumString(key),
        })),
    ];
  }, [OrderRetailerType]);

  const onExportCustomerOrderSalesReport = (): void => {
    setExportLoading(true);
    downloadCustomerOrderSalesCsv({
      fromDate: FormatDateHelper.formatCalendarDateToISO(customerSalesFromDate),
      toDate: FormatDateHelper.formatCalendarDateToISO(customerSalesToDate),
      regionId: selectedRegion,
    }, (response: any) => {
      const file = response.customerOrderSales;
      GenerateDownload(file.content, file.filename);
      setExportLoading(false);
    });
  };

  const onExportCustomerItemSalesReport = (): void => {
    setExportLoading(true);
    downloadCustomerItemSalesCsv(
      {
        fromDate: FormatDateHelper.formatCalendarDateToISO(customerSalesFromDate),
        toDate: FormatDateHelper.formatCalendarDateToISO(customerSalesToDate),
        regionId: selectedRegion,
      },
      (response: any) => {
        const file = response.customerItemSales;
        GenerateDownload(file.content, file.filename);
        setExportLoading(false);
      },
    );
  };

  const onPinValidation = (value: string) => {
    if (value !== '') {
      if (!parseInt(value, 10)) {
        return false;
      }
    }

    setPin(value);
  };

  return (
    <div className="reports card">
      {(exportLoading || (retailers && retailers.loading)) && <LoadingThrobber />}
      <h4>Reports</h4>
      <p>Quick select reports for management</p>
      {isAdmin(userDetails) || isAccountManager(userDetails) ? (
      <>
        <Button
          type="button"
          className="secondary"
          text="Download All Confirmed Orders"
          onClick={() => setShowConfirmedOrderModal(true)}
        />
        <div className="confirmedOrders">
          <Modal
              header="Confirmed Orders Report"
              isOpen={showConfirmedOrderModal}
              onClose={async () => {
                try {
                  await downloadConfirmedOrderCsv(null, orderType);
                } catch (error) {
                  console.log('An error has occurred while downloading CSV', error);
                }
                resetStates();
              }}
              onCancel={() => {
                resetStates();
              }}
              buttonText="Download"
          >
            <p>Order Type</p>
              <Dropdown
                className="order-type"
                options={orderTypeOptions}
                selectedValue={orderType}
                label=""
                name="orderType"
                onChange={value => setOrderType(value.value as string)}
              />
          </Modal>
        </div>
      </>
      ) : null}
      {isAdmin(userDetails) || isAccountManager(userDetails) ? (
        <>
          <Button
            type="button"
            className="secondary"
            text="Customer Order Sales"
            onClick={() => setShowOrderSalesModal(true)}
          />
          <Modal
            header="Customer Order Sales Report"
            isOpen={showOrderSalesModal}
            onClose={() => {
              onExportCustomerOrderSalesReport();
              resetStates();
            }}
            onCancel={() => {
              resetStates();
            }}
            buttonText="Export"
          >
            {isAccountManager(userDetails) ? null : (
              <>
                <p>Region</p>
                <Dropdown
                  className="customer-sales-region"
                  options={regionOptions}
                  selectedValue={selectedRegion}
                  label=""
                  name="orderSalesRegion"
                  onChange={(value: DropdownOption) => setSelectedRegion(value.value as number)}
                />
              </>
            )}
            <p>Delivery Date</p>
            <StyledCalendar
              key={calendarKey}
              value={customerSalesDate}
              onChange={(date) => {
                if (Array.isArray(date)) {
                  setCustomerSalesFromDate(date[0]);
                  setCustomerSalesToDate(date[1]);
                } else {
                  setCustomerSalesFromDate(date);
                  setCustomerSalesToDate(date);
                }
              }}
              selectRange={true}
              {...{ locale: 'en-GB' }}
            />
          </Modal>
        </>
      ) : null}
      {isAdmin(userDetails) || isAccountManager(userDetails) ? (
        <>
          <Button
            type="button"
            className="secondary"
            text="Customer Item Sales"
            onClick={() => setShowItemSalesModal(true)}
          />
          <Modal
            header="Customer Item Sales Report"
            isOpen={showItemSalesModal}
            onClose={() => {
              onExportCustomerItemSalesReport();
              resetStates();
            }}
            onCancel={() => {
              resetStates();
            }}
            buttonText="Export"
          >
            {isAccountManager(userDetails) ? null : (
              <>
                <p>Region</p>
                <Dropdown
                  className="customer-sales-region"
                  options={regionOptions}
                  selectedValue={selectedRegion}
                  label=""
                  name="orderItemsRegion"
                  onChange={(value: DropdownOption) => setSelectedRegion(value.value as number)}
                />
              </>
            )}
            <p>Delivery Date</p>
            <StyledCalendar
              key={calendarKey}
              value={customerSalesDate}
              onChange={(date) => {
                if (Array.isArray(date)) {
                  setCustomerSalesFromDate(date[0]);
                  setCustomerSalesToDate(date[1]);
                } else {
                  setCustomerSalesFromDate(date);
                  setCustomerSalesToDate(date);
                }
              }}
              selectRange={true}
              {...{ locale: 'en-GB' }}
            />
          </Modal>
        </>
      ) : null}
      {isAdmin(userDetails) || isAccountManager(userDetails) ? (
        <>
          <span
            className="bulk-manifest-title"
            title={
              enableExportBulkManifest() ? '' : 'The daily order cut-off (10am) has not yet passed.'
            }
          >
            <Button
              type="button"
              text="Bulk Manifest"
              className="secondary"
              onClick={() => onExportBulkManifest()}
              disabled={!enableExportBulkManifest()}
            />
          </span>
          <Button
            type="button"
            className="secondary"
            text="Completed Orders"
            onClick={() => {
              getLoadedRetailers();
              setShowCompletedOrdersModal(true);
            }}
          />
          <Modal
            header="Completed Orders Report"
            isOpen={showCompletedOrdersModal}
            onClose={() => {
              onExportCompletedOrdersReport();
              resetStates();
            }}
            onCancel={() => {
              resetStates();
            }}
            buttonText="Export"
          >
            {isAdmin(userDetails) ? (
              <>
                <p>Region</p>
                <Dropdown
                  className="completed-orders-region"
                  options={regionOptions}
                  selectedValue={selectedRegion}
                  label=""
                  name="CompletedOrdersRegion"
                  onChange={(value: DropdownOption) => setSelectedRegion(value.value as number)}
                />
                <p>Customer</p>
                <Dropdown
                  className="completed-orders-retailers"
                  options={retailerOptions}
                  selectedValue={selectedRetailer}
                  label=""
                  name="CompletedOrdersRetailers"
                  onChange={(value: DropdownOption) => setSelectedRetailer(value.value as string)}
                />
              </>
            ) : null}

            <p>PIN</p>
            <input
              type="text"
              value={pin}
              placeholder="Pin"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPinValidation(e.target.value)}
              name="pin-entry"
              maxLength={10}
              className="pin-entry"
            />
            <p>Delivery Date</p>
            <StyledCalendar
              key={calendarKey}
              value={customerSalesDate}
              minDate={minimumReportDate}
              maxDate={maximumReportDate}
              onChange={(date) => {
                if (Array.isArray(date)) {
                  setCustomerSalesFromDate(date[0]);
                  setCustomerSalesToDate(date[1]);
                } else {
                  setCustomerSalesFromDate(date);
                  setCustomerSalesToDate(date);
                }
              }}
              selectRange={true}
              {...{ locale: 'en-GB' }}
            />
          </Modal>
        </>
      ) : null}
    </div>
  );
};

const mapStateToProps = (state: IStoreState) => {
  return {
    userDetails: state.userDetails,
    confirmedOrdersCsvDownload: state.confirmedOrders,
    regions: state.regions.regions,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    downloadConfirmedOrdersCsv: (parameters: IConfirmedOrdersParams) =>
      dispatch(getConfirmedOrdersReportRequest(parameters)),
    downloadCompletedOrdersReport: (parameters: ICompletedOrdersParams, onSuccess: (data: any) => void) =>
      dispatch(getCompletedOrdersReportRequest(parameters, onSuccess)),
    clearConfirmedOrdersCsvDownload: () => dispatch(resetReportData()),
    downloadCustomerOrderSalesCsv: (parameters: ICustomerOrderSalesReportParams, onSuccess: (data: any) => void) =>
      dispatch(getCustomerOrderSalesReportRequest(parameters, onSuccess)),
    downloadCustomerItemSalesCsv: (parameters: ICustomerItemSalesReportParams, onSuccess: (data: any) => void) =>
      dispatch(getCustomerItemSalesReportRequest(parameters, onSuccess)),
    exportBulkManifest: (onSuccess: (data: any) => void) =>
      dispatch(getProcessedBulkOrdersReportRequest(onSuccess)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ReportsCard);
